/**
 * History service for trade history and agent activity (T155).
 */
import { api, type RequestOptions } from './api';
import type { Trade } from '@/types/trading';
import type { AgentDecision } from '@/stores/agentStore';

export interface TradeHistoryFilters {
  symbol?: string;
  side?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
  [key: string]: unknown;
}

export interface TradeHistoryResponse {
  trades: Trade[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface PeriodStats {
  period: string;
  totalTrades: number;
  totalVolume: number;
  totalCommission: number;
  realizedPnl: number;
  winRate: number;
}

export interface DecisionHistoryFilters {
  decisionType?: string;
  outcome?: string;
  symbol?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortOrder?: string;
  page?: number;
  pageSize?: number;
  [key: string]: unknown;
}

export interface DecisionHistoryResponse {
  decisions: AgentDecision[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Build query string from filters.
 */
function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      // Convert camelCase to snake_case
      const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      searchParams.append(snakeKey, String(value));
    }
  }

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Transform API response to camelCase.
 */
function transformTradeResponse(data: Record<string, unknown>): TradeHistoryResponse {
  const trades = ((data.trades as Record<string, unknown>[]) || []).map((trade) => ({
    id: trade.id as string,
    order_id: trade.order_id as string,
    user_id: '',
    symbol: trade.symbol as string,
    side: trade.side as 'buy' | 'sell',
    quantity: Number(trade.quantity),
    price: Number(trade.price),
    total_value: Number(trade.value),
    commission: Number(trade.commission),
    executed_at: trade.created_at as string,
    created_at: trade.created_at as string,
  }));

  return {
    trades,
    total: data.total as number,
    page: data.page as number,
    pageSize: data.page_size as number,
    totalPages: data.total_pages as number,
  };
}

/**
 * Transform stats response to camelCase.
 */
function transformStatsResponse(data: Record<string, unknown>): PeriodStats {
  return {
    period: data.period as string,
    totalTrades: data.total_trades as number,
    totalVolume: Number(data.total_volume),
    totalCommission: Number(data.total_commission),
    realizedPnl: Number(data.realized_pnl),
    winRate: Number(data.win_rate),
  };
}

/**
 * Transform decision response to camelCase.
 */
function transformDecisionResponse(data: Record<string, unknown>): DecisionHistoryResponse {
  const decisions = ((data.decisions as Record<string, unknown>[]) || []).map((decision) => ({
    id: decision.id as string,
    // Default to 'hold' when the backend hasn't stamped decision_type yet.
    // Prevents a runtime TypeError downstream when rendering `.toUpperCase()`.
    decisionType: (decision.decision_type ?? 'hold') as AgentDecision['decisionType'],
    symbol: decision.symbol as string,
    quantity: decision.quantity ? Number(decision.quantity) : null,
    price: decision.price ? Number(decision.price) : null,
    notional: decision.notional ? Number(decision.notional) : null,
    reasoning: decision.reasoning as string,
    confidence: Number(decision.confidence),
    outcome: decision.outcome as AgentDecision['outcome'],
    createdAt: decision.created_at as string,
  }));

  return {
    decisions,
    total: data.total as number,
    page: data.page as number,
    pageSize: data.page_size as number,
    totalPages: data.total_pages as number,
  };
}

export const historyService = {
  /**
   * Get trade history with optional filters.
   */
  async getTrades(filters?: TradeHistoryFilters): Promise<TradeHistoryResponse> {
    const queryString = buildQueryString(filters || {});
    const response = await api.get<Record<string, unknown>>(`/history/trades${queryString}`);
    return transformTradeResponse(response);
  },

  /**
   * Get period statistics.
   */
  async getStats(period: string = 'week'): Promise<PeriodStats> {
    const response = await api.get<Record<string, unknown>>(`/history/stats?period=${period}`);
    return transformStatsResponse(response);
  },

  /**
   * Export trade history as CSV.
   */
  async exportHistory(filters?: Omit<TradeHistoryFilters, 'page' | 'pageSize'>): Promise<Blob> {
    const queryString = buildQueryString(filters || {});
    const response = await fetch(`/api/v1/history/export${queryString}`, {
      headers: {
        Authorization: `Bearer ${api.getToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to export history');
    }

    return response.blob();
  },

  /**
   * Get agent decisions with optional filters.
   *
   * `options` forwards per-request overrides to the API client. Background
   * pollers (e.g. the globally-mounted pending-approvals badge) pass
   * `suppressUnauthorized` so a 401 fails soft instead of tripping the app-wide
   * hard logout for a page whose own session check already succeeded.
   */
  async getAgentDecisions(
    filters?: DecisionHistoryFilters,
    options?: RequestOptions,
  ): Promise<DecisionHistoryResponse> {
    const queryString = buildQueryString(filters || {});
    const response = await api.get<Record<string, unknown>>(
      `/agent/decisions${queryString}`,
      options,
    );
    return transformDecisionResponse(response);
  },
};

export default historyService;
